import React, { useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  formatPedidoAmount,
  getPedidoCliente,
  getPedidoDoc,
  getPedidoTipocontMeta,
  summarizePedidoDoc,
  TIPOCONT_META,
} from "./pedidosVentaUtils";

const ESTADO_META = {
  abierto: { label: "Abierto", color: "#ed6c02" },
  servido: { label: "Servido", color: "#2e7d32" },
  cerrado: { label: "Cerrado", color: "#607080" },
};

function Metric({ label, value, color, hint }) {
  return (
    <Box
      sx={{
        p: 1.1,
        borderRadius: 2,
        border: "1px solid",
        borderColor: alpha(color || "#607080", 0.28),
        bgcolor: alpha(color || "#607080", 0.08),
        minWidth: 0,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 800,
          color: color || "text.secondary",
          letterSpacing: 0.3,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "1.05rem",
          fontVariantNumeric: "tabular-nums",
          color: color || "text.primary",
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {hint}
        </Typography>
      ) : null}
    </Box>
  );
}

const PedidoVentaDocDialog = ({ open, onClose, row, rows = [] }) => {
  const theme = useTheme();
  const summary = useMemo(() => summarizePedidoDoc(rows), [rows]);
  if (!row) return null;
  const estado = ESTADO_META[summary.estado] || ESTADO_META.cerrado;
  const cliente = getPedidoCliente(row);
  const doc = getPedidoDoc(row);
  const tipocontChips = (summary.tipoconts || []).map(
    (tipocont) =>
      TIPOCONT_META[tipocont] || getPedidoTipocontMeta({ TIPOCONT: tipocont }),
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
            {doc || "Pedido"}
          </Typography>
          <Chip
            size="small"
            label={estado.label}
            sx={{
              fontWeight: 800,
              color: estado.color,
              bgcolor: alpha(estado.color, 0.14),
              border: "1px solid",
              borderColor: alpha(estado.color, 0.35),
            }}
          />
          {tipocontChips.map((meta) => (
            <Chip
              key={meta.tipocont || meta.key}
              size="small"
              label={meta.label}
              sx={{
                fontWeight: 800,
                color: meta.color,
                bgcolor: alpha(meta.color, 0.14),
                border: "1px solid",
                borderColor: alpha(meta.color, 0.35),
              }}
            />
          ))}
        </Stack>
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={700}
          sx={{ mt: 0.4 }}
        >
          {cliente} · {summary.count} {summary.count === 1 ? "línea" : "líneas"}
          {summary.opsFromDirecto
            ? " · € comercial = Grupo · servicio/factura = Directo"
            : ""}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            mt: 1.25,
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr 1fr",
              sm: "repeat(4, minmax(0, 1fr))",
            },
            gap: 1,
          }}
        >
          <Metric
            label="Pedido €"
            value={formatPedidoAmount(summary.venta) || "0,00"}
            color={theme.palette.primary.main}
            hint={
              summary.ventaParte > 0
                ? `Grupo · Directo ${formatPedidoAmount(summary.ventaParte) || "0,00"} €`
                : undefined
            }
          />
          <Metric
            label="Facturado €"
            value={formatPedidoAmount(summary.facturado) || "0,00"}
            color="#64dd01"
            hint={summary.opsFromDirecto ? "vía Directo" : undefined}
          />
          <Metric
            label="Pendiente €"
            value={formatPedidoAmount(summary.pendiente) || "0,00"}
            color={theme.palette.error.main}
            hint={`${summary.ptes.toLocaleString("es-ES")} uds ptes`}
          />
          <Metric
            label="Unidades"
            value={`${summary.serv.toLocaleString("es-ES")} / ${summary.un.toLocaleString("es-ES")}`}
            hint={
              summary.opsFromDirecto
                ? "Servidas / pedidas (Directo)"
                : "Servidas / pedidas"
            }
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          sx={{ textTransform: "none", fontWeight: 800 }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PedidoVentaDocDialog;
